/**
 * The arithmetic the town screen is allowed to do, and the contract it takes.
 *
 * ## Not one game rule is decided here
 *
 * The screen is handed a character whose every number has **already been
 * resolved by the caller**, out of `packages/core`: the experience curve is
 * `experienceForLevel` / `experienceToNextLevel`, and the purse's cap is
 * `goldCapacity(level)`. Nothing in this file re-derives either. There is no
 * `level * GOLD_PER_LEVEL` and no experience table, and there must never be
 * one: a rule written here as well as in core is the same rule in two places,
 * and the second copy is the one that goes stale (`CLAUDE.md`, and the same
 * argument `character-view.ts` makes at length).
 *
 * What is left is arithmetic about *pixels and words*: a percentage for a bar,
 * a grouped number for a readout, a sentence for a screen reader. That is
 * presentation, and it lives in a module of its own for the two reasons
 * `hud-model.ts` gives — the divide is the part that can actually be wrong, and
 * this package's unit runner has no DOM by default, so arithmetic kept out of
 * the markup is provable directly.
 *
 * ## It duplicates a little of `hud-model.ts`, deliberately
 *
 * A meter and a max-level span are the same shape the HUD needed. Sharing a
 * module with the panels that already exist is explicitly *not* this file's job
 * — those were written by other agents, and reconciling three sets of styles
 * and helpers into one is a later, deliberate step. A duplicated clamp is
 * cheaper than a merge conflict over a shared file, and unlike a duplicated
 * *rule* it cannot go stale: there is no authority for it to fall out of step
 * with.
 */

import { RUN_VISIBILITY, type RunVisibility } from '@d2/schema';
import { type ReactNode } from 'react';

import { type JoinRefusal, describeJoinFailure } from '../game/join-refusal';
import { type RunToll, runEndedText } from '../game/run-end';

/** A number that is safe to put in front of a player. Anything else is zero. */
const finiteOrZero = (value: number): number => (Number.isFinite(value) ? value : 0);

/** The character the town screen draws. Every number arrives already resolved. */
export interface TownCharacterView {
  readonly name: string;
  /** 'melee' | 'caster' | 'ranged' — core's own vocabulary. */
  readonly characterClass: string;
  readonly level: number;
  /**
   * Already resolved by the caller. The screen computes no curve.
   *
   * **Absent means the caller does not know**, which is a real state and not a
   * zero: the roster this screen is built from carries no experience at all
   * (`@d2/schema`'s `CharacterSummary` has a level and gold and nothing else to
   * spend), and a level is not a substitute for it — one is banked and one is
   * what the banking bought. Both fields go together: half an answer is still
   * not a proportion.
   */
  readonly experienceIntoLevel?: number;
  /** 0 at max level, absent when nobody has resolved it. See `experienceIntoLevel`. */
  readonly experienceForNextLevel?: number;
  readonly gold: number;
  /** The cap `gold` is measured against — a function of level, resolved by the caller. */
  readonly goldCapacity: number;
}

/**
 * How the last press of Descend turned out — the two ways it can be over.
 *
 * A union rather than two props, because they are **mutually exclusive by
 * construction and one live region draws them**. A screen handed both a refusal
 * and a run that ended would have to invent a precedence between them, and the
 * one it invented would be wrong on the day the caller forgot to clear the
 * other. Here there is nothing to clear: a value replaces a value.
 *
 * The two are genuinely different events and not two flavours of failure, which
 * is the distinction `game/run-end.ts` is about:
 *
 *   - **`join-refused`** — no run happened. The room would not open one, for a
 *     reason that is not about who this player is (`game/join-refusal.ts`), and
 *     the answer is another press.
 *   - **`run-ended`** — a run happened and is over. The player died, the seat
 *     left with close code 4200, and nothing failed at any point.
 *
 * Each carries the one fact its screen cannot work out for itself: a refusal
 * carries *why*, and a death carries *what it cost*.
 */
export type RunOutcome =
  | { readonly kind: 'join-refused'; readonly refusal: JoinRefusal }
  | { readonly kind: 'run-ended'; readonly toll: RunToll };

/** The refusal, as the one thing town is told about a run that never opened. */
export const joinRefused = (refusal: JoinRefusal): RunOutcome => ({ kind: 'join-refused', refusal });

/**
 * A run that is over, and the bill for it.
 *
 * The toll is carried rather than a sentence, exactly as `joinRefused` carries a
 * refusal rather than the words for one: the caller knows what the room charged
 * and `game/run-end.ts` knows how to say it. It is **not** optional and there is
 * no constant for a death with no toll attached — the room sends the message
 * before every close and zero is a real bill (`NOTHING_LOST`), so an absent one
 * would be a state nothing can produce and everything downstream would have to
 * hold.
 */
export const runEnded = (toll: RunToll): RunOutcome => ({ kind: 'run-ended', toll });

export interface TownScreenProps {
  readonly character: TownCharacterView;
  /**
   * Start a run, and say **who may find it**.
   *
   * The screen still does not know what a floor is; what it now knows is that a
   * run is either advertised or reachable only by its code, because that is a
   * question only the player can answer. It is an argument rather than a
   * separate prop for the reason `onDescend` takes no event: the press and the
   * choice are one decision, and a caller reading a choice it was told about on
   * an earlier render is a caller that can descend under the wrong one.
   *
   * Being listed publishes this character's name and level to anybody holding
   * an account (`@d2/schema`'s `RunListing`), so it is never a default — see
   * `DEFAULT_VISIBILITY`.
   */
  readonly onDescend: (visibility: RunVisibility) => void;
  readonly onSignOut: () => void;
  /**
   * True while a run is being opened, so the button cannot be pressed twice.
   *
   * Absent is the ordinary state — a player standing in town — and is the same
   * as `false`. The brief this was built from described the flag the other way
   * round in its comment while stating the behaviour plainly beside it ("cannot
   * be pressed again while `descending` is true"); the behaviour is what is
   * implemented, and the discrepancy is flagged rather than silently resolved.
   */
  readonly descending?: boolean;
  /**
   * How the last press of Descend ended, or `null` because none of them has —
   * a run the room would not open, or a run that is over.
   *
   * **A value, not a sentence.** The caller knows what happened; this screen
   * decides what a player is told about it and where — the words are
   * `describeJoinFailure`'s and `runEndedText`'s, and they land in the live
   * region the busy note already uses. A caller that passed prose would be a
   * caller that could say *"unauthorised"* over a town screen, which is the
   * whole failure this milestone is undoing.
   *
   * The screen does nothing else with it. It does not disable the button, does
   * not retry, and keeps no memory of it: an outcome is cleared by the caller on
   * the next press, and a screen that latched one would be a screen with a dead
   * Descend button and no way out of town (`isDescending`). That is as true of a
   * death as of a refusal — the way back down after dying is the same one press.
   */
  readonly outcome?: RunOutcome | null;
  /**
   * The vendor's counter, when the caller has one to put here.
   *
   * A node rather than a bundle of props, and that is the point: town gives the
   * vendor a **place to stand** and knows nothing else about it. The counter
   * talks to the API and holds a token (`VendorCounter.tsx`), and a town screen
   * that took those as props would be a screen that could get an exchange wrong.
   *
   * Absent is a real state and not a stub: it says *this caller has no vendor*,
   * and the screen then names one among the services it does not offer, exactly
   * as it did before one existed. That is what keeps the tripwire below honest —
   * a placeholder that could never be wired to anything, beside a seam that is.
   */
  readonly vendor?: ReactNode;
  /**
   * The board of other people's runs, when the caller has one to put here.
   *
   * A node for the same reason the vendor's counter is one: what talks to a
   * server holds a token, and a town screen that took a run list as a prop
   * would be a town screen that could get a join wrong. Town gives it a place to
   * stand, immediately under the button that starts a run of your own, and
   * knows nothing else about it.
   *
   * Absent is a real state — the renderer sandbox, and any caller with no
   * session — and the screen simply has no board.
   */
  readonly lobby?: ReactNode;
}

/** A quantity against a capacity, resolved for painting. */
export interface Meter {
  /** The amount held, as it will be read out. Never below zero — and never clamped down. */
  readonly current: number;
  /** The capacity it is measured against. Never below zero. */
  readonly maximum: number;
  /** `current / maximum` in `0..1`, clamped. Zero for a meter with no capacity. */
  readonly ratio: number;
  /** The same thing in `0..100`, to two decimals, which is what a CSS length wants. */
  readonly percent: number;
}

/**
 * How full a meter is.
 *
 * The clamp is on the *ratio* only, and that asymmetry is the point. A bar
 * painted past 100% escapes its own trough, so the width is clamped; the
 * readout is not, because `current` here is money and a purse shown as
 * `10,000 / 10,000` when it holds twelve thousand is a lie about a resource the
 * player spends. Core's `gainGold` already caps the purse, so over-cap is a
 * state this screen should never see — which is exactly why it must not quietly
 * paper over it if it does.
 *
 * A capacity of zero is the case that would otherwise crash the layout: `0 / 0`
 * is `NaN`, and `width: NaN%` is a declaration a browser silently drops, which
 * paints an empty trough as a full one.
 */
export const meterOf = (current: number, maximum: number): Meter => {
  const held = Math.max(0, finiteOrZero(current));
  const capacity = Math.max(0, finiteOrZero(maximum));
  const ratio = capacity === 0 ? 0 : Math.min(held / capacity, 1);

  return { current: held, maximum: capacity, ratio, percent: Math.round(ratio * 10_000) / 100 };
};

/**
 * Grouped in `en-US` explicitly rather than in the visitor's locale.
 *
 * The rest of the client is written in one language, so a purse that switched
 * to `120.000` on a German browser while every word around it stayed English
 * would be a half-translation. Localising the client is a decision of its own,
 * and it is not this screen's to make.
 */
const GROUPED = new Intl.NumberFormat('en-US');

/** `120,000` — a six-figure purse a player can read without counting digits. */
export const countLabel = (value: number): string => GROUPED.format(Math.round(finiteOrZero(value)));

/** `1,234 / 120,000` — the meter in whole units, for a player who wants the number. */
export const meterReadout = (meter: Meter): string => `${countLabel(meter.current)} / ${countLabel(meter.maximum)}`;

/**
 * What the experience bar is showing.
 *
 * Three states rather than one, and each of the other two is a lie in place of
 * the third:
 *
 *   - a **max-level** character is not a character at 0% of the next level. The
 *     caller says the level spans no experience, so there is no proportion to
 *     take and no divide to do, and drawing that as an empty bar would read as
 *     *you have earned nothing*.
 *   - an **unknown** experience is not zero and it is not max level either. The
 *     caller could not resolve it — today, because the roster does not carry it
 *     — and a screen that guessed would tell a level 12 character either that
 *     they have just levelled or that they have finished the game. This is the
 *     same call `character-view.ts` makes for the HUD: *"`null` rather than
 *     zeroes, because the panel has no way to say 'unknown'"*.
 */
export type ExperienceReadout =
  | { readonly kind: 'levelling'; readonly meter: Meter }
  | { readonly kind: 'max-level' }
  | { readonly kind: 'unknown' };

/** The span this character is inside, the news that there is not one, or that nobody knows. */
export const experienceReadoutOf = (
  character: Pick<TownCharacterView, 'experienceIntoLevel' | 'experienceForNextLevel'>,
): ExperienceReadout => {
  const into = character.experienceIntoLevel;
  const span = character.experienceForNextLevel;

  // First, and not as a fall-through: `undefined > 0` is false, so an unknown
  // span read in the order below would be reported as a character with nothing
  // left to earn.
  if (into === undefined || span === undefined) return { kind: 'unknown' };

  // `> 0` and not `!== 0`: it also refuses a negative span and — because every
  // comparison with `NaN` is false — a span that is not a number at all.
  return span > 0 ? { kind: 'levelling', meter: meterOf(into, span) } : { kind: 'max-level' };
};

/** The purse against the cap the caller resolved. No cap is computed here. */
export const goldMeterOf = (character: Pick<TownCharacterView, 'gold' | 'goldCapacity'>): Meter =>
  meterOf(character.gold, character.goldCapacity);

/** Said instead of a proportion, when there is no next level. */
export const MAX_LEVEL_TEXT = 'Maximum level';

/**
 * Said instead of a proportion, when nobody resolved one.
 *
 * It names *where* the gap is rather than apologising for it, because the gap
 * is a real one a player can otherwise only read as a bug: their experience is
 * on the character sheet a run draws, and the roster town is built from does
 * not carry it (`TODO.md`).
 */
export const EXPERIENCE_UNKNOWN_TEXT = 'Not known in town yet';

/** The purse in words. A bar width and a colour say none of this. */
export const describeGold = (meter: Meter): string =>
  `${countLabel(meter.current)} of ${countLabel(meter.maximum)} gold carried`;

/** The level's progress in words, or the news that there is no more of it — or none at all. */
export const describeExperience = (readout: ExperienceReadout): string => {
  switch (readout.kind) {
    case 'max-level':
      return MAX_LEVEL_TEXT;
    case 'unknown':
      return EXPERIENCE_UNKNOWN_TEXT;
    case 'levelling':
      return `${countLabel(readout.meter.current)} of ${countLabel(readout.meter.maximum)} experience towards the next level`;
  }
};

/** A level as it is shown — a whole number, and never `level NaN`. */
export const levelLabel = (level: number): number => Math.max(0, Math.round(finiteOrZero(level)));

/**
 * Who is standing in town, in one line.
 *
 * The class is printed as core spells it. No table of pretty names is kept
 * here: `CharacterScreen.tsx` already holds one blurb per class, a second copy
 * would be the one nobody updates on the day a fourth class lands, and an
 * unknown class must still render rather than fall through a lookup.
 */
export const describeCharacter = (
  character: Pick<TownCharacterView, 'name' | 'characterClass' | 'level'>,
): string => `${character.name}, level ${levelLabel(character.level)} ${character.characterClass}`;

/**
 * Who may find a run, when nobody has said.
 *
 * **Private**, and it is the same answer the server gives an absent field
 * (`@d2/schema`'s `RUN_VISIBILITY`, `apps/game`'s `resolveVisibility`) — which
 * is the point: this screen states what its control shows, a caller with no
 * control states nothing, and both produce the same run. Being listed publishes
 * a character's name and level to strangers, and that has to be something
 * somebody asked for rather than the state a screen happened to open in.
 */
export const DEFAULT_VISIBILITY: RunVisibility = RUN_VISIBILITY.private;

/**
 * What the choice is called, above the two options.
 *
 * Phrased as the question a player is actually answering. *"Visibility"* is the
 * wire's word for it and says nothing to anybody standing in a town.
 */
export const VISIBILITY_LEGEND = 'Who can find this run';

/**
 * The two options, in the order they are offered — the safe one first.
 *
 * Taken from `RUN_VISIBILITY` rather than written out, so a third kind of
 * visibility could not be added to the contract and quietly go unoffered here.
 */
export const VISIBILITY_CHOICES: readonly RunVisibility[] = Object.freeze([
  RUN_VISIBILITY.private,
  RUN_VISIBILITY.public,
]);

/** What each option is called. The wire's own words are not words for a player. */
export const visibilityLabel = (visibility: RunVisibility): string =>
  visibility === RUN_VISIBILITY.public ? 'Public' : 'Private';

/**
 * What choosing it actually does, in one line beside the control.
 *
 * The public one **names the cost out loud**, because it is a cost a player
 * cannot see coming: a listed run publishes the name and level of everybody in
 * the party to anybody holding an account, and nobody would guess that from the
 * word *public*. The private one names what it buys — the code — which is
 * otherwise the least discoverable half of this feature.
 */
export const visibilityNoteOf = (visibility: RunVisibility): string =>
  visibility === RUN_VISIBILITY.public
    ? 'Anybody can find this run, and your character’s name and level are listed for them.'
    : 'Nobody can find this run. You will be given a code to read out to whoever you want in it.';

/** What the button says when a run can be asked for. */
export const DESCEND_LABEL = 'Descend';

/** What it says while one is being opened. */
export const DESCEND_BUSY_LABEL = 'Descending…';

/** What is announced while one is being opened, for a reader that sees no greyed button. */
export const DESCEND_BUSY_NOTE = 'Opening a run…';

/**
 * Whether a run is already on its way.
 *
 * The caller owns this, and the screen keeps no latch of its own. A latch here
 * would outlive the caller's knowledge of the run: a descend that is refused
 * leaves the prop `false` and the screen must offer the button again, and a
 * latch nothing releases is a dead button on the only screen with a way out of
 * town.
 */
export const isDescending = (descending: boolean | undefined): boolean => descending === true;

export const descendLabelOf = (descending: boolean | undefined): string =>
  isDescending(descending) ? DESCEND_BUSY_LABEL : DESCEND_LABEL;

/**
 * What the last press of Descend came to, in words.
 *
 * Both sentences are somebody else's. This is a dispatch and not an author: the
 * figures in a death's sentence are the room's, taken off the outcome and passed
 * on untouched, and there is deliberately no purse in scope here to subtract
 * them from.
 */
export const describeOutcome = (outcome: RunOutcome): string =>
  outcome.kind === 'run-ended' ? runEndedText(outcome.toll) : describeJoinFailure(outcome.refusal);

/**
 * The one sentence the live region carries, out of the three things that could
 * want it.
 *
 * A run that is opening wins, and not by accident: the press that produced that
 * render *is* the answer to whatever came before it, so a refusal — or a death
 * — shown beside it is stale news. Two live sentences in one polite region would
 * also be read out as one run-on line by the software that most needs either of
 * them.
 *
 * Empty string rather than `null`, so the element keeps its height and its
 * identity across renders — a live region that is added to the document at the
 * moment it has something to say is a live region a reader may never announce.
 */
export const statusNoteOf = (descending: boolean | undefined, outcome: RunOutcome | null | undefined): string => {
  if (isDescending(descending)) return DESCEND_BUSY_NOTE;

  return outcome === null || outcome === undefined ? '' : describeOutcome(outcome);
};

/**
 * The three duties `docs/GAME-DESIGN.md` gives a town, in the order it gives
 * them.
 *
 * Named on the screen so a player knows town is not finished, and named as
 * **text rather than as controls** so nobody mistakes a placeholder for a seam
 * that is ready.
 */
export const TOWN_SERVICES = Object.freeze(['Vendor', 'Stash', 'Repair'] as const);

/**
 * What town still does not offer.
 *
 * The vendor opened on 2026-08-14 and it brought repairs with it — they are two
 * of its three exchanges, not a fourth service — so a caller with a counter
 * leaves exactly one name on this list. The **stash** is the one, and it stays
 * for a reason rather than for want of a button: the API will sell out of it,
 * this client offers only the pack (`vendor-view.ts`), and there is no route
 * between the two containers at all. Its size, its tab count and whether it is
 * shared across characters are also still open (`TODO.md` #25).
 *
 * The list shrinking rather than disappearing is what keeps the tripwire in
 * `town-screen.test.tsx` doing its job: whatever is named here still has to hold
 * no control, and a later agent wiring a Stash button would be inventing a
 * contract from the wrong end again.
 */
export const unopenedServices = (vendorOpen: boolean): readonly string[] =>
  vendorOpen ? ['Stash'] : [...TOWN_SERVICES];

/** Why the names above are names and not buttons. */
export const servicesNoteOf = (vendorOpen: boolean): string =>
  vendorOpen
    ? 'The stash is not open yet — nothing can move between it and your pack.'
    : 'Not open yet — none of the three has a server to talk to.';
